import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiAdmin as api } from "@/lib/api";
import { reportError } from "@/lib/errors";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue
} from "@/components/ui/select";
import { EzLogo } from "@/pages/LandingPage";
import {
  Users, Inbox, FileSearch, UserCheck, UserX,
  Download, RefreshCw, LogOut, FileDown, Gauge, CheckSquare, Sparkles, Loader2, Trash2, ExternalLink, Plus, X, Filter, ArrowUpDown, ArrowUp, ArrowDown, BrainCircuit, GraduationCap,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Mail, Copy, Check, Settings, Save, ArrowRightCircle, LifeBuoy, Database
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 100;

function getToken() {
  return localStorage.getItem("ez_admin_token");
}

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getScoreColor(score) {
  if (score >= 65) return "text-emerald-600 bg-emerald-50 border-emerald-200";
  if (score >= 40) return "text-amber-600 bg-amber-50 border-amber-200";
  return "text-red-500 bg-red-50 border-red-200";
}

function getScoreRingColor(score) {
  if (score >= 65) return "#10b981";
  if (score >= 40) return "#d97706";
  return "#ef4444";
}

const STATUS_CONFIG = {
  new: { label: "New", color: "bg-blue-100 text-blue-700 border-blue-200" },
  reviewing: { label: "Reviewing", color: "bg-amber-100 text-amber-700 border-amber-200" },
  interviewed: { label: "Interviewed", color: "bg-purple-100 text-purple-700 border-purple-200" },
  "In RPS": { label: "In RPS", color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  "In Final Interview": { label: "In Final Interview", color: "bg-violet-100 text-violet-700 border-violet-200" },
  "Offer": { label: "Offer", color: "bg-teal-100 text-teal-700 border-teal-200" },
  accepted: { label: "Accepted", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700 border-red-200" },
};

const FILTER_FIELDS = [
  { key: "full_name", label: "Name", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "score_total", label: "Score", type: "number" },
  { key: "status", label: "Status", type: "enum", options: ["new", "reviewing", "interviewed", "In RPS", "In Final Interview", "Offer", "accepted", "rejected"] },
  { key: "submitted_at", label: "Date Applied", type: "date" },
  { key: "college_name", label: "College", type: "text" },
  { key: "can_work_25_hours", label: "Can work 25+ hrs", type: "boolean" },
  { key: "okay_with_1099", label: "OK with 1099", type: "boolean" },
  { key: "ashby_candidate_id", label: "In Ashby", type: "exists" },
];

const OPS_BY_TYPE = {
  text: [
    { key: "contains", label: "contains" },
    { key: "eq", label: "equals" },
    { key: "starts", label: "starts with" },
    { key: "not_contains", label: "does not contain" },
  ],
  number: [
    { key: "gte", label: ">=" },
    { key: "lte", label: "<=" },
    { key: "eq", label: "=" },
    { key: "gt", label: ">" },
    { key: "lt", label: "<" },
  ],
  date: [
    { key: "gte", label: "on or after" },
    { key: "lte", label: "on or before" },
    { key: "eq", label: "on" },
  ],
  enum: [
    { key: "eq", label: "is" },
    { key: "neq", label: "is not" },
  ],
  boolean: [
    { key: "is_true", label: "is Yes" },
    { key: "is_false", label: "is No" },
  ],
  exists: [
    { key: "exists", label: "exists" },
    { key: "not_exists", label: "does not exist" },
  ],
};

function parseFiltersFromURL(searchParams) {
  const filters = [];
  searchParams.getAll("f").forEach(raw => {
    const parts = raw.split("__");
    if (parts.length >= 2) {
      const [field, op, ...rest] = parts;
      filters.push({ field, op, value: rest.join("__") });
    }
  });
  return filters;
}

function filtersToURL(filters) {
  const params = new URLSearchParams();
  filters.forEach(f => {
    const val = f.value || "";
    params.append("f", `${f.field}__${f.op}__${val}`);
  });
  return params;
}

// Mirrors the backend guard: short `contains` fragments on these fields cause
// expensive scans server-side and are dropped, so don't bother sending them.
const TEXT_CONTAINS_FIELDS = new Set(["full_name", "email", "college_name"]);
function isShortTextContains(f) {
  return TEXT_CONTAINS_FIELDS.has(f.field) && f.op === "contains" && (f.value || "").trim().length < 3;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [stats, setStats] = useState(null);
  const [filters, setFiltersState] = useState(() => parseFiltersFromURL(searchParams));
  const [draftFilters, setDraftFilters] = useState(() => parseFiltersFromURL(searchParams));
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [scoringId, setScoringId] = useState(null);
  const [ashbyLoadingId, setAshbyLoadingId] = useState(null);
  const [sortCol, setSortCol] = useState(null); // null | "score" | "date"
  const [sortDir, setSortDir] = useState(null); // null | "asc" | "desc"
  const [aiRescoreStatus, setAiRescoreStatus] = useState(null);
  const [collegeRescoreStatus, setCollegeRescoreStatus] = useState(null);
  const [ashbyBackfillRunning, setAshbyBackfillRunning] = useState(false);
  const [ashbyBackfillResult, setAshbyBackfillResult] = useState(null);
  const [indeedEmailBackfillStatus, setIndeedEmailBackfillStatus] = useState(null);
  const [indeedEmailBackfillResult, setIndeedEmailBackfillResult] = useState(null);
  const [sentInvitesData, setSentInvitesData] = useState(null);
  const [sentInvitesLoading, setSentInvitesLoading] = useState(false);
  const [sentInvitesCopied, setSentInvitesCopied] = useState(false);
  const [backfillCopied, setBackfillCopied] = useState(false);
  const [purgeResumesRunning, setPurgeResumesRunning] = useState(false);
  const [purgeResumesResult, setPurgeResumesResult] = useState(null);
  const [purgeCopied, setPurgeCopied] = useState(false);
  const [jobTypeFilter, setJobTypeFilter] = useState("all"); // "all" | "guide" | "sales" | "prescreens"
  const [prescreens, setPrescreens] = useState(null);
  const [prescreensLoading, setPrescreensLoading] = useState(false);
  const debounceRef = useRef(null);

  const setFilters = useCallback((updater) => {
    setFiltersState(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      setSearchParams(prev => {
        const p = filtersToURL(next);
        const appParam = prev.get("app");
        if (appParam) p.set("app", appParam);
        return p;
      }, { replace: true });
      setDraftFilters(next);
      return next;
    });
    setCurrentPage(1);
    setCheckedIds(new Set());
    setSelectAllMatching(false);
  }, [setSearchParams]);

  const applyFilters = useCallback(() => {
    setFilters(draftFilters);
  }, [draftFilters, setFilters]);

  const openApp = useCallback(async (app) => {
    setSelected(app); // show immediately with list data
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set("app", app.id);
      return p;
    }, { replace: true });
    // Fetch full detail (includes AI reasoning fields)
    try {
      const res = await api.get(`${API}/applications/${app.id}`, { headers: authHeaders() });
      setSelected(res.data);
    } catch {
      // keep the slim list data if detail fetch fails
    }
  }, [setSearchParams]);

  const closeApp = useCallback(() => {
    setSelected(null);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete("app");
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  // Verify token on mount
  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }
    api.get(`${API}/admin/verify`, { headers: authHeaders() })
      .then(() => setAuthChecked(true))
      .catch(() => {
        localStorage.removeItem("ez_admin_token");
        localStorage.removeItem("ez_admin_user");
        navigate("/admin/login", { replace: true });
      });
  }, [navigate]);

  const fetchData = useCallback(async (page, sortBy, sortDirection, activeFilters, jtFilter) => {
    setLoading(true);
    try {
      const params = {
        page: page || 1,
        page_size: PAGE_SIZE,
      };
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_dir = sortDirection || "desc";
      }
      // Add filter params + job type filter. Drop short contains predicates —
      // the server drops them anyway and they'd otherwise hit an expensive scan.
      const fParams = (activeFilters || [])
        .filter(f => !isShortTextContains(f))
        .map(f => `${f.field}__${f.op}__${f.value || ""}`);
      if (jtFilter && jtFilter !== "all") {
        fParams.push(`job_type__eq__${jtFilter}`);
      }

      const appsRes = await api.get(`${API}/applications`, {
          params: { ...params, f: fParams },
          paramsSerializer: (p) => {
            const s = new URLSearchParams();
            Object.entries(p).forEach(([k, v]) => {
              if (Array.isArray(v)) v.forEach(item => s.append(k, item));
              else s.append(k, v);
            });
            return s.toString();
          },
          headers: authHeaders(),
        });
      const data = appsRes.data;
      setApplications(data.items || []);
      setTotalCount(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setCurrentPage(data.page || 1);

      // /api/stats fetch intentionally disabled — endpoint is too slow and
      // the stat cards aren't critical for the dashboard UX. The cards JSX
      // remains in place but is gated on `stats !== null`, so it stays hidden
      // until/unless we re-enable the fetch.
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ez_admin_token");
        localStorage.removeItem("ez_admin_user");
        navigate("/admin/login", { replace: true });
        return;
      }
      reportError(err, "Failed to load data", true);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Trigger fetch when page, sort, or filters change
  useEffect(() => {
    if (!authChecked) return;
    if (jobTypeFilter === "prescreens") return;  // separate loader below
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchData(currentPage, sortCol, sortDir, filters, jobTypeFilter);
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [authChecked, currentPage, sortCol, sortDir, filters, jobTypeFilter, fetchData]);

  // Load pre-screen records when that tab is active. Kept separate from
  // fetchData because the shape (indeed_inbound_emails vs applications) and
  // controls (no filters/sort/pagination for now) differ.
  const fetchPrescreens = useCallback(async () => {
    setPrescreensLoading(true);
    try {
      const res = await api.get(`${API}/admin/indeed-prescreens`, { headers: authHeaders() });
      setPrescreens(res.data);
    } catch (err) {
      reportError(err, "Could not load pre-screens", true);
      setPrescreens({ prescreens: [], truncated: false, limit: 0 });
    } finally {
      setPrescreensLoading(false);
    }
  }, []);
  useEffect(() => {
    if (authChecked && jobTypeFilter === "prescreens") fetchPrescreens();
  }, [authChecked, jobTypeFilter, fetchPrescreens]);

  // Auto-open app from URL param — fetch directly by ID (works regardless of page)
  const initialAppOpened = useRef(false);
  useEffect(() => {
    if (!authChecked) return;
    const appId = searchParams.get("app");
    if (appId && !initialAppOpened.current) {
      initialAppOpened.current = true;
      api.get(`${API}/applications/${appId}`, { headers: authHeaders() })
        .then(res => setSelected(res.data))
        .catch((err) => reportError(err, "Could not find that application", true));
    }
  }, [authChecked, searchParams]);

  const toggleSort = useCallback((col) => {
    setCheckedIds(new Set());
    setSelectAllMatching(false);
    if (sortCol !== col) { setSortCol(col); setSortDir("desc"); }
    else if (sortDir === "desc") setSortDir("asc");
    else { setSortCol(null); setSortDir(null); }
    setCurrentPage(1);
  }, [sortCol, sortDir]);

  const goToPage = useCallback((page) => {
    const p = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(p);
    setCheckedIds(new Set());
    setSelectAllMatching(false);
  }, [totalPages]);

  const refresh = useCallback(() => {
    fetchData(currentPage, sortCol, sortDir, filters, jobTypeFilter);
  }, [fetchData, currentPage, sortCol, sortDir, filters, jobTypeFilter]);

  const updateStatus = async (appId, newStatus) => {
    try {
      await api.patch(`${API}/applications/${appId}/status`, { status: newStatus }, { headers: authHeaders() });
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`);
      refresh();
      if (selected?.id === appId) {
        setSelected(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      reportError(err, "Failed to update status", true);
    }
  };

  const downloadResume = (appId) => {
    const token = getToken();
    // Open in new tab with token param as fallback
    window.open(`${API}/applications/${appId}/resume?token=${token}`, "_blank");
  };

  const handleRescore = async () => {
    try {
      await api.post(`${API}/admin/rescore`, {}, { headers: authHeaders() });
      toast.success("All applications re-scored");
      refresh();
    } catch (err) {
      reportError(err, "Failed to re-score", true);
    }
  };

  const handleAiRescore = async () => {
    try {
      const res = await api.post(`${API}/admin/rescore-ai`, {}, { headers: authHeaders() });
      if (res.data.running) {
        toast.info(`AI rescore in progress: ${res.data.done}/${res.data.total}`);
      } else if (res.data.remaining === 0) {
        toast.success(res.data.message);
        return;
      } else {
        toast.success(res.data.message);
      }
      setAiRescoreStatus({ running: true, total: res.data.remaining || 0, done: 0, errors: 0 });
      // Start polling progress
      const poll = setInterval(async () => {
        try {
          const statusRes = await api.get(`${API}/admin/rescore-ai/status`, { headers: authHeaders() });
          setAiRescoreStatus(statusRes.data);
          if (!statusRes.data.running) {
            clearInterval(poll);
            const { done, errors, remaining } = statusRes.data;
            toast.success(`AI rescore batch done: ${done} scored${errors ? `, ${errors} errors` : ""}${remaining ? `, ${remaining} still need rescoring` : ""}`);
            refresh();
            if (!remaining) setTimeout(() => setAiRescoreStatus(null), 5000);
          }
        } catch {
          clearInterval(poll);
        }
      }, 3000);
    } catch (err) {
      reportError(err, "Failed to start AI rescore", true);
    }
  };

  const handleCollegeRescore = async () => {
    try {
      const res = await api.post(`${API}/admin/rescore-colleges`, {}, { headers: authHeaders() });
      if (res.data.running) {
        toast.info(`College rescore in progress: ${res.data.done}/${res.data.total}`);
      } else {
        toast.success(res.data.message);
      }
      setCollegeRescoreStatus({ running: true, total: res.data.total || 0, done: 0 });
      const poll = setInterval(async () => {
        try {
          const s = await api.get(`${API}/admin/rescore-colleges/status`, { headers: authHeaders() });
          setCollegeRescoreStatus(s.data);
          if (!s.data.running) {
            clearInterval(poll);
            toast.success(`College rescore done: ${s.data.ranked} rank-matched, ${s.data.llm} LLM-scored${s.data.errors ? `, ${s.data.errors} errors` : ""}`);
            refresh();
            setTimeout(() => setCollegeRescoreStatus(null), 5000);
          }
        } catch { clearInterval(poll); }
      }, 2000);
    } catch (err) {
      reportError(err, "Failed to start college rescore", true);
    }
  };

  const handleAshbyBackfill = async () => {
    const choice = window.prompt(
      "Backfill Ashby orphans from the last 2 weeks.\n\n" +
      'Type "dry" to dry-run (Mixpanel events only — no Ashby writes).\n' +
      'Type "real" to run for real.\n\n' +
      "Anything else (or Cancel) aborts."
    );
    if (choice !== "dry" && choice !== "real") {
      toast.info("Backfill cancelled");
      return;
    }
    const dryRun = choice === "dry";
    setAshbyBackfillRunning(true);
    try {
      const res = await api.post(
        `${API}/admin/backfill-ashby-orphans`,
        { dry_run: dryRun },
        { headers: authHeaders() }
      );
      const data = res.data;
      setAshbyBackfillResult(data);
      if (data.dry_run) {
        if (data.scanned === 0) {
          toast.success("Dry run — no Ashby orphans found in the last 2 weeks");
        } else {
          toast.success(`Dry run — ${data.would_place} candidate(s) would be backfilled (Mixpanel events fired)`);
        }
      } else {
        const { scanned, placed, skipped_existing, failed } = data;
        if (scanned === 0) {
          toast.success("No Ashby orphans found in the last 2 weeks");
        } else {
          toast.success(
            `Backfill done — scanned ${scanned}, placed ${placed}, skipped (already in Ashby) ${skipped_existing}` +
            (failed ? `, failed ${failed}` : "")
          );
        }
      }
      refresh();
    } catch (err) {
      reportError(err, "Ashby backfill failed", true);
    } finally {
      setAshbyBackfillRunning(false);
    }
  };

  const handleIndeedEmailBackfill = async () => {
    const choice = window.prompt(
      "Backfill the Indeed invite email to applicants from the last 24 hours who never received it and haven't applied.\n\n" +
      'Type "dry" to dry-run (counts only — no emails sent).\n' +
      'Type "real" to send the emails for real.\n\n' +
      "Anything else (or Cancel) aborts."
    );
    if (choice !== "dry" && choice !== "real") {
      toast.info("Email backfill cancelled");
      return;
    }
    const dryRun = choice === "dry";
    try {
      await api.post(
        `${API}/admin/backfill-indeed-invite-emails`,
        { dry_run: dryRun },
        { headers: authHeaders() }
      );
      toast.info(dryRun ? "Email backfill dry run started…" : "Email backfill started…");
      setIndeedEmailBackfillStatus({ running: true, dry_run: dryRun });
      const poll = setInterval(async () => {
        try {
          const s = await api.get(`${API}/admin/backfill-indeed-invite-emails/status`, { headers: authHeaders() });
          setIndeedEmailBackfillStatus(s.data);
          if (!s.data.running) {
            clearInterval(poll);
            setIndeedEmailBackfillStatus(null);
            if (s.data.error) {
              toast.error(`Email backfill failed: ${s.data.error}`);
            } else {
              setIndeedEmailBackfillResult(s.data);
            }
            refresh();
          }
        } catch { clearInterval(poll); }
      }, 2000);
    } catch (err) {
      reportError(err, "Failed to start Indeed email backfill", true);
    }
  };

  const handleViewSentInvites = async () => {
    setSentInvitesLoading(true);
    try {
      const res = await api.get(`${API}/admin/indeed-invite-emails?window_hours=48`, { headers: authHeaders() });
      setSentInvitesData(res.data);
    } catch (err) {
      reportError(err, "Failed to load sent invite emails", true);
    } finally {
      setSentInvitesLoading(false);
    }
  };

  const handlePurgeOldResumes = async () => {
    const choice = window.prompt(
      "Purge resume_data on applications older than 28 days.\n\n" +
      'Type "dry" to dry-run (counts only, no writes).\n' +
      'Type "real" to run for real.\n\n' +
      "Anything else (or Cancel) aborts."
    );
    if (choice !== "dry" && choice !== "real") {
      toast.info("Purge cancelled");
      return;
    }
    const dryRun = choice === "dry";
    setPurgeResumesRunning(true);
    try {
      const res = await api.post(
        `${API}/admin/purge-old-resumes`,
        { dry_run: dryRun },
        { headers: authHeaders() }
      );
      const data = res.data;
      setPurgeResumesResult(data);
      const mb = (data.would_free_bytes ?? data.freed_bytes ?? 0) / 1024 / 1024;
      if (data.dry_run) {
        if (data.scanned === 0) {
          toast.success("Dry run — nothing to purge");
        } else {
          toast.success(`Dry run — ${data.would_purge} resume(s) would be purged, freeing ${mb.toFixed(2)} MB`);
        }
      } else if (data.scanned === 0) {
        toast.success("Nothing to purge");
      } else {
        toast.success(`Purged ${data.purged} resume(s) — freed ~${mb.toFixed(2)} MB`);
        refresh();
      }
    } catch (err) {
      reportError(err, "Purge failed", true);
    } finally {
      setPurgeResumesRunning(false);
    }
  };

  const scoreResume = async (appId) => {
    setScoringId(appId);
    try {
      const res = await api.post(`${API}/applications/${appId}/score-resume`, {}, { headers: authHeaders() });
      toast.success(`Resume scored: ${res.data.llm_score}/100`);
      // Refresh the selected application detail
      const updatedApp = await api.get(`${API}/applications/${appId}`, { headers: authHeaders() });
      setSelected(updatedApp.data);
      refresh();
    } catch (err) {
      reportError(err, err.response?.data?.detail || "Failed to score resume", true);
    } finally {
      setScoringId(null);
    }
  };

  const deleteApplication = async (appId) => {
    if (!window.confirm("Are you sure you want to permanently delete this application?")) return;
    try {
      await api.delete(`${API}/applications/${appId}`, { headers: authHeaders() });
      toast.success("Application deleted");
      closeApp();
      refresh();
    } catch (err) {
      reportError(err, "Failed to delete application", true);
    }
  };

  const bulkDelete = async () => {
    const count = bulkSelectionCount;
    if (!window.confirm(`Are you sure you want to permanently delete ${count} application(s)? This cannot be undone.`)) return;
    try {
      await api.post(`${API}/applications/bulk-delete`, bulkPayload(), { headers: authHeaders() });
      toast.success(`Deleted ${count} application(s)`);
      setCheckedIds(new Set());
      setSelectAllMatching(false);
      refresh();
    } catch (err) {
      reportError(err, "Failed to delete applications", true);
    }
  };

  const createInAshby = async (appId) => {
    setAshbyLoadingId(appId);
    try {
      await api.post(`${API}/applications/${appId}/create-ashby`, {}, { headers: authHeaders() });
      toast.success("Candidate created in Ashby");
      const updatedApp = await api.get(`${API}/applications/${appId}`, { headers: authHeaders() });
      setSelected(updatedApp.data);
      refresh();
    } catch (err) {
      reportError(err, err.response?.data?.detail || "Failed to create in Ashby", true);
    } finally {
      setAshbyLoadingId(null);
    }
  };

  const [willoLoadingId, setWilloLoadingId] = useState(null);
  const moveToWillo = async (appId) => {
    setWilloLoadingId(appId);
    try {
      const res = await api.post(`${API}/applications/${appId}/move-to-willo`, {}, { headers: authHeaders() });
      if (res.data.success) {
        toast.success("Moved to Willo stage");
        const updatedApp = await api.get(`${API}/applications/${appId}`, { headers: authHeaders() });
        setSelected(updatedApp.data);
        refresh();
      }
    } catch (err) {
      reportError(err, err.response?.data?.detail || "Failed to move to Willo", true);
    } finally {
      setWilloLoadingId(null);
    }
  };

  const [bulkAshbyLoading, setBulkAshbyLoading] = useState(false);
  const [bulkHqiLoading, setBulkHqiLoading] = useState(false);
  const [bulkEmailLoading, setBulkEmailLoading] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  const bulkCreateAshby = async () => {
    setBulkAshbyLoading(true);
    try {
      const res = await api.post(`${API}/applications/bulk-create-ashby`, bulkPayload(), { headers: authHeaders() });
      const { job_id, total } = res.data;
      toast.info(`Uploading ${total} candidate${total > 1 ? "s" : ""} to Ashby...`);

      // Poll for status
      const poll = async () => {
        for (let i = 0; i < 300; i++) { // up to ~10 min
          await new Promise(r => setTimeout(r, 2000));
          try {
            const status = await api.get(`${API}/applications/bulk-ashby-status/${job_id}`, { headers: authHeaders() });
            const { done, created, skipped, failed, processed, total: t } = status.data;
            if (done) {
              let msg = `Created ${created} in Ashby`;
              if (skipped) msg += `, ${skipped} already existed`;
              if (failed) msg += `, ${failed} failed`;
              toast.success(msg);
              setCheckedIds(new Set());
              setSelectAllMatching(false);
              setBulkAshbyLoading(false);
              refresh();
              return;
            }
          } catch {
            // poll error, keep trying
          }
        }
        setBulkAshbyLoading(false);
      };
      poll();
    } catch (err) {
      reportError(err, "Failed to start Ashby upload", true);
      setBulkAshbyLoading(false);
    }
  };

  const bulkUploadHqi = async () => {
    setBulkHqiLoading(true);
    try {
      const res = await api.post(`${API}/applications/bulk-upload-hqi`, bulkPayload(), { headers: authHeaders() });
      const { job_id, total } = res.data;
      toast.info(`Uploading ${total} candidate${total > 1 ? "s" : ""} to High Quality role...`);

      const poll = async () => {
        for (let i = 0; i < 300; i++) {
          await new Promise(r => setTimeout(r, 2000));
          try {
            const status = await api.get(`${API}/applications/bulk-upload-hqi-status/${job_id}`, { headers: authHeaders() });
            const { done, created, skipped, failed } = status.data;
            if (done) {
              let msg = `Uploaded ${created} to High Quality role`;
              if (skipped) msg += `, ${skipped} already uploaded`;
              if (failed) msg += `, ${failed} failed`;
              toast.success(msg);
              setCheckedIds(new Set());
              setSelectAllMatching(false);
              setBulkHqiLoading(false);
              refresh();
              return;
            }
          } catch {
            // poll error, keep trying
          }
        }
        setBulkHqiLoading(false);
      };
      poll();
    } catch (err) {
      reportError(err, "Failed to start High Quality upload", true);
      setBulkHqiLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("ez_admin_token");
    localStorage.removeItem("ez_admin_user");
    navigate("/admin/login", { replace: true });
  };

  const bulkDraftEmails = async () => {
    setBulkEmailLoading(true);
    try {
      const res = await api.post(`${API}/applications/bulk-draft-emails`, bulkPayload(), { headers: authHeaders() });
      const { job_id, total } = res.data;
      toast.info(`Drafting emails for ${total} candidate${total > 1 ? "s" : ""}...`);

      const poll = async () => {
        for (let i = 0; i < 300; i++) {
          await new Promise(r => setTimeout(r, 2000));
          try {
            const status = await api.get(`${API}/applications/bulk-draft-emails-status/${job_id}`, { headers: authHeaders() });
            const { done, drafted, skipped, failed } = status.data;
            if (done) {
              let msg = `Drafted ${drafted} email${drafted !== 1 ? "s" : ""}`;
              if (skipped) msg += `, ${skipped} already had drafts`;
              if (failed) msg += `, ${failed} failed`;
              toast.success(msg);
              setCheckedIds(new Set());
              setSelectAllMatching(false);
              setBulkEmailLoading(false);
              refresh();
              return;
            }
          } catch {
            // poll error, keep trying
          }
        }
        setBulkEmailLoading(false);
      };
      poll();
    } catch (err) {
      reportError(err, "Failed to start email drafting", true);
      setBulkEmailLoading(false);
    }
  };

  const copyEmailToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setEmailCopied(true);
      toast.success("Email copied to clipboard");
      setTimeout(() => setEmailCopied(false), 2000);
    }).catch(() => {
      toast.error("Failed to copy");
    });
  };

  // ── Email Config Editor ──────────────────────────────────────────
  const [showEmailConfig, setShowEmailConfig] = useState(false);
  const [emailConfig, setEmailConfig] = useState(null);
  const [emailConfigSaving, setEmailConfigSaving] = useState(false);

  const loadEmailConfig = async () => {
    try {
      const res = await api.get(`${API}/admin/email-config`, { headers: authHeaders() });
      setEmailConfig(res.data);
    } catch (err) {
      reportError(err, "Failed to load email config", true);
    }
  };

  const saveEmailConfig = async () => {
    setEmailConfigSaving(true);
    try {
      await api.put(`${API}/admin/email-config`, emailConfig, { headers: authHeaders() });
      toast.success("Email config saved");
      setShowEmailConfig(false);
    } catch (err) {
      reportError(err, "Failed to save email config", true);
    } finally {
      setEmailConfigSaving(false);
    }
  };

  const openEmailConfig = () => {
    setEmailConfig(null);
    setShowEmailConfig(true);
    loadEmailConfig();
  };

  const exportCSV = () => {
    const token = getToken();
    const params = new URLSearchParams({ token });
    window.open(`${API}/applications/export/csv?${params.toString()}`, "_blank");
  };

  const toggleCheck = (id) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); setSelectAllMatching(false); }
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (checkedIds.size === applications.length) {
      setCheckedIds(new Set());
      setSelectAllMatching(false);
    } else {
      setCheckedIds(new Set(applications.map(a => a.id)));
    }
  };

  // Helper: build payload for bulk actions
  const bulkPayload = () => {
    if (selectAllMatching) {
      return {
        select_all: true,
        filters: filters
          .filter(f => !isShortTextContains(f))
          .map(f => `${f.field}__${f.op}__${f.value || ""}`),
      };
    }
    return { ids: [...checkedIds] };
  };

  const bulkSelectionCount = selectAllMatching ? totalCount : checkedIds.size;

  const bulkUpdateStatus = async (newStatus) => {
    const count = bulkSelectionCount;
    if (!window.confirm(`Update ${count} application(s) to ${STATUS_CONFIG[newStatus].label}?`)) return;
    try {
      const payload = { ...bulkPayload(), status: newStatus };
      await api.patch(`${API}/applications/bulk-status`, payload, { headers: authHeaders() });
      toast.success(`Updated ${count} application(s) to ${STATUS_CONFIG[newStatus].label}`);
      setCheckedIds(new Set());
      setSelectAllMatching(false);
      refresh();
    } catch (err) {
      reportError(err, "Failed to bulk update status", true);
    }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    let hh = d.getHours();
    const ampm = hh >= 12 ? "pm" : "am";
    hh = hh % 12 || 12;
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${mm}/${dd} ${hh}:${min} ${ampm}`;
  };

  if (!authChecked) return null;

  const statCards = stats ? [
    { label: "Total", value: stats.total, icon: <Users className="w-5 h-5" />, color: "text-ez-teal", bg: "bg-slate-50" },
    { label: "New", value: stats.new, icon: <Inbox className="w-5 h-5" />, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Reviewing", value: stats.reviewing, icon: <FileSearch className="w-5 h-5" />, color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Accepted", value: stats.accepted, icon: <UserCheck className="w-5 h-5" />, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Rejected", value: stats.rejected, icon: <UserX className="w-5 h-5" />, color: "text-red-600", bg: "bg-red-50" },
  ] : [];

  return (
    <div className="min-h-screen bg-ez-surface" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <EzLogo size="h-6" />
            <span className="text-slate-300">|</span>
            <span className="text-sm font-semibold text-slate-600 font-heading">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="rescore-btn"
              variant="outline"
              size="sm"
              onClick={handleRescore}
              className="rounded-lg"
            >
              <Gauge className="w-4 h-4 mr-1.5" /> Re-score All
            </Button>
            <Button
              data-testid="ai-rescore-btn"
              variant="outline"
              size="sm"
              onClick={handleAiRescore}
              disabled={aiRescoreStatus?.running}
              className="rounded-lg text-violet-600 border-violet-200 hover:bg-violet-50"
            >
              {aiRescoreStatus?.running ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> {aiRescoreStatus.done}/{aiRescoreStatus.total}</>
              ) : (
                <><BrainCircuit className="w-4 h-4 mr-1.5" /> AI Re-score All</>
              )}
            </Button>
            <Button
              data-testid="college-rescore-btn"
              variant="outline"
              size="sm"
              onClick={handleCollegeRescore}
              disabled={collegeRescoreStatus?.running}
              className="rounded-lg text-teal-600 border-teal-200 hover:bg-teal-50"
            >
              {collegeRescoreStatus?.running ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> {collegeRescoreStatus.done}/{collegeRescoreStatus.total}</>
              ) : (
                <><GraduationCap className="w-4 h-4 mr-1.5" /> Re-score Colleges</>
              )}
            </Button>
            <Button
              data-testid="ashby-backfill-btn"
              variant="outline"
              size="sm"
              onClick={handleAshbyBackfill}
              disabled={ashbyBackfillRunning}
              className="rounded-lg text-amber-600 border-amber-200 hover:bg-amber-50"
            >
              {ashbyBackfillRunning ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Backfilling…</>
              ) : (
                <><LifeBuoy className="w-4 h-4 mr-1.5" /> Backfill Ashby</>
              )}
            </Button>
            <Button
              data-testid="indeed-email-backfill-btn"
              variant="outline"
              size="sm"
              onClick={handleIndeedEmailBackfill}
              disabled={indeedEmailBackfillStatus?.running}
              className="rounded-lg text-indigo-600 border-indigo-200 hover:bg-indigo-50"
            >
              {indeedEmailBackfillStatus?.running ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Emailing…</>
              ) : (
                <><Mail className="w-4 h-4 mr-1.5" /> Backfill Invite Emails</>
              )}
            </Button>
            <Button
              data-testid="view-sent-invites-btn"
              variant="outline"
              size="sm"
              onClick={handleViewSentInvites}
              disabled={sentInvitesLoading}
              className="rounded-lg text-slate-600 border-slate-200 hover:bg-slate-50"
            >
              {sentInvitesLoading ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Loading…</>
              ) : (
                <><Inbox className="w-4 h-4 mr-1.5" /> Sent Invite Emails</>
              )}
            </Button>
            <Button
              data-testid="purge-resumes-btn"
              variant="outline"
              size="sm"
              onClick={handlePurgeOldResumes}
              disabled={purgeResumesRunning}
              className="rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50"
            >
              {purgeResumesRunning ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Purging…</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-1.5" /> Purge Old Resumes</>
              )}
            </Button>
            <Button
              data-testid="email-config-btn"
              variant="outline"
              size="sm"
              onClick={openEmailConfig}
              className="rounded-lg text-slate-600 border-slate-200 hover:bg-slate-50"
            >
              <Settings className="w-4 h-4 mr-1.5" /> Email Prompt
            </Button>
            <DbDebugPanel />
            <Button
              data-testid="refresh-btn"
              variant="outline"
              size="sm"
              onClick={refresh}
              className="rounded-lg"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Job Type Tabs */}
        <div className="flex items-center gap-2 mb-6" data-testid="job-type-tabs">
          {[
            { key: "all", label: "All Jobs" },
            { key: "guide", label: "Health Guides" },
            { key: "sales", label: "Sales" },
            { key: "prescreens", label: "Pre-Screens" },
          ].map(tab => (
            <button
              key={tab.key}
              data-testid={`job-tab-${tab.key}`}
              onClick={() => { setJobTypeFilter(tab.key); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                jobTypeFilter === tab.key
                  ? "bg-[#1B3A4B] text-white shadow-sm"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Stats */}
        {jobTypeFilter === "prescreens" ? (
          <PrescreensTable
            prescreens={prescreens}
            loading={prescreensLoading}
            onRefresh={fetchPrescreens}
          />
        ) : (
        <>
        {/* Filter + Table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="applications-table-container">
          <div className="px-6 py-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-ez-teal font-heading">Applications</h2>
              <span className="text-xs text-slate-400 font-body" data-testid="pagination-info">
                {totalCount > 0 ? `${((currentPage - 1) * PAGE_SIZE) + 1}–${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount}` : "0"}
              </span>
              {filters.length > 0 && (
                <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 font-semibold" data-testid="filter-count-badge">
                  {filters.length} filter{filters.length > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Pagination controls */}
              {totalPages > 1 && (
                <div className="flex items-center gap-1" data-testid="pagination-controls">
                  <Button data-testid="pagination-first" variant="outline" size="sm" onClick={() => goToPage(1)} disabled={currentPage <= 1} className="rounded-lg h-7 w-7 p-0">
                    <ChevronsLeft className="w-3.5 h-3.5" />
                  </Button>
                  <Button data-testid="pagination-prev" variant="outline" size="sm" onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1} className="rounded-lg h-7 w-7 p-0">
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </Button>
                  {(() => {
                    const pages = [];
                    let start = Math.max(1, currentPage - 2);
                    let end = Math.min(totalPages, start + 4);
                    if (end - start < 4) start = Math.max(1, end - 4);
                    for (let i = start; i <= end; i++) pages.push(i);
                    return pages.map(p => (
                      <Button
                        key={p}
                        data-testid={`pagination-page-${p}`}
                        variant={p === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => goToPage(p)}
                        className={`rounded-lg h-7 w-7 p-0 text-xs font-semibold ${p === currentPage ? "bg-ez-teal text-white hover:bg-ez-teal/90" : ""}`}
                      >
                        {p}
                      </Button>
                    ));
                  })()}
                  <Button data-testid="pagination-next" variant="outline" size="sm" onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages} className="rounded-lg h-7 w-7 p-0">
                    <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                  <Button data-testid="pagination-last" variant="outline" size="sm" onClick={() => goToPage(totalPages)} disabled={currentPage >= totalPages} className="rounded-lg h-7 w-7 p-0">
                    <ChevronsRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
              <span className="text-slate-200">|</span>
              <Button
                data-testid="export-csv-btn"
                variant="outline"
                size="sm"
                onClick={exportCSV}
                className="rounded-lg text-ez-teal border-slate-200 hover:bg-slate-50"
              >
                <FileDown className="w-4 h-4 mr-1.5" /> Export CSV
              </Button>
              <Button
                data-testid="add-filter-btn"
                variant="outline"
                size="sm"
                onClick={() => setDraftFilters(prev => [...prev, { field: "full_name", op: "contains", value: "" }])}
                className="rounded-lg text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                <Plus className="w-4 h-4 mr-1" /> Add filter
              </Button>
              {filters.length > 0 && (
                <button
                  data-testid="clear-all-filters-btn"
                  onClick={() => setFilters([])}
                  className="text-xs text-slate-400 hover:text-red-500 underline font-body transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Filter rows */}
          {draftFilters.length > 0 && (
            <div className="px-6 py-3 bg-slate-50/80 border-b border-slate-100 space-y-2" data-testid="filter-builder">
              {draftFilters.map((f, idx) => {
                const fieldCfg = FILTER_FIELDS.find(ff => ff.key === f.field) || FILTER_FIELDS[0];
                const ops = OPS_BY_TYPE[fieldCfg.type] || [];
                const needsValue = !["boolean", "exists"].includes(fieldCfg.type);
                return (
                  <div key={idx} className="flex items-center gap-2 flex-wrap" data-testid={`filter-row-${idx}`}>
                    {idx === 0 ? (
                      <span className="text-xs text-slate-400 font-body w-12">Where</span>
                    ) : (
                      <span className="text-xs text-blue-500 font-semibold font-body w-12">AND</span>
                    )}
                    <select
                      data-testid={`filter-field-${idx}`}
                      value={f.field}
                      onChange={e => {
                        const newField = e.target.value;
                        const newFieldCfg = FILTER_FIELDS.find(ff => ff.key === newField);
                        const newOps = OPS_BY_TYPE[newFieldCfg.type];
                        setDraftFilters(prev => prev.map((pf, i) =>
                          i === idx ? { field: newField, op: newOps[0].key, value: "" } : pf
                        ));
                      }}
                      className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:ring-1 focus:ring-blue-300 focus:border-blue-300 outline-none font-body"
                    >
                      {FILTER_FIELDS.map(ff => (
                        <option key={ff.key} value={ff.key}>{ff.label}</option>
                      ))}
                    </select>
                    <select
                      data-testid={`filter-op-${idx}`}
                      value={f.op}
                      onChange={e => setDraftFilters(prev => prev.map((pf, i) =>
                        i === idx ? { ...pf, op: e.target.value } : pf
                      ))}
                      className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:ring-1 focus:ring-blue-300 focus:border-blue-300 outline-none font-body"
                    >
                      {ops.map(o => (
                        <option key={o.key} value={o.key}>{o.label}</option>
                      ))}
                    </select>
                    {needsValue && (
                      fieldCfg.type === "enum" ? (
                        <select
                          data-testid={`filter-value-${idx}`}
                          value={f.value}
                          onChange={e => setDraftFilters(prev => prev.map((pf, i) =>
                            i === idx ? { ...pf, value: e.target.value } : pf
                          ))}
                          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:ring-1 focus:ring-blue-300 focus:border-blue-300 outline-none font-body"
                        >
                          <option value="">Select...</option>
                          {(fieldCfg.options || []).map(opt => (
                            <option key={opt} value={opt}>{STATUS_CONFIG[opt]?.label || opt}</option>
                          ))}
                        </select>
                      ) : fieldCfg.type === "date" ? (
                        <input
                          data-testid={`filter-value-${idx}`}
                          type="date"
                          value={f.value}
                          onChange={e => setDraftFilters(prev => prev.map((pf, i) =>
                            i === idx ? { ...pf, value: e.target.value } : pf
                          ))}
                          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:ring-1 focus:ring-blue-300 focus:border-blue-300 outline-none font-body"
                        />
                      ) : (<>
                        <input
                          data-testid={`filter-value-${idx}`}
                          type={fieldCfg.type === "number" ? "number" : "text"}
                          value={f.value}
                          onChange={e => setDraftFilters(prev => prev.map((pf, i) =>
                            i === idx ? { ...pf, value: e.target.value } : pf
                          ))}
                          onKeyDown={e => { if (e.key === "Enter") applyFilters(); }}
                          placeholder="value..."
                          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:ring-1 focus:ring-blue-300 focus:border-blue-300 outline-none font-body w-36"
                        />
                        {isShortTextContains(f) && f.value && (
                          <span className="text-[11px] text-amber-600 font-body w-full">
                            Type at least 3 characters
                          </span>
                        )}
                      </>)
                    )}
                    <button
                      data-testid={`filter-remove-${idx}`}
                      onClick={() => {
                        setDraftFilters(prev => prev.filter((_, i) => i !== idx));
                        setFilters(prev => prev.filter((_, i) => i !== idx));
                      }}
                      className="p-1 text-slate-300 hover:text-red-400 transition-colors rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
              <div className="flex items-center gap-2 pt-1">
                <Button
                  data-testid="apply-filters-btn"
                  size="sm"
                  onClick={applyFilters}
                  className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs px-4"
                >
                  Apply Filters
                </Button>
                <Button
                  data-testid="clear-filters-btn"
                  variant="ghost"
                  size="sm"
                  onClick={() => { setDraftFilters([]); setFilters([]); }}
                  className="rounded-lg text-xs text-slate-500"
                >
                  Clear All
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-3" />
              <p className="text-slate-500 font-body">Loading applications...</p>
            </div>
          ) : applications.length === 0 ? (
            <div className="p-12 text-center" data-testid="no-applications">
              <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-body">
                {totalCount === 0 && filters.length === 0 ? "No applications found" : `No applications match ${filters.length} filter${filters.length > 1 ? "s" : ""}`}
              </p>
            </div>
          ) : (
            <>
              {checkedIds.size > 0 && (
                <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex flex-col gap-2" data-testid="bulk-action-bar">
                  {/* Select-all-matching banner */}
                  {!selectAllMatching && checkedIds.size === applications.length && totalPages > 1 && (
                    <div className="text-sm text-slate-600 font-body" data-testid="select-all-banner">
                      All {applications.length} on this page are selected.{" "}
                      <button
                        data-testid="select-all-matching-btn"
                        onClick={() => setSelectAllMatching(true)}
                        className="text-blue-600 font-semibold hover:underline"
                      >
                        Select all {totalCount} matching results.
                      </button>
                    </div>
                  )}
                  {selectAllMatching && (
                    <div className="text-sm text-blue-700 font-semibold font-body" data-testid="all-matching-banner">
                      All {totalCount} matching results are selected.
                    </div>
                  )}
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-sm font-semibold text-ez-teal font-heading">
                      {bulkSelectionCount} selected
                    </span>
                  <span className="text-slate-300">|</span>
                  <span className="text-xs text-slate-500 font-body mr-1">Set status:</span>
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <button
                      key={key}
                      data-testid={`bulk-status-${key}`}
                      onClick={() => bulkUpdateStatus(key)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors duration-150 ${cfg.color} hover:opacity-80`}
                    >
                      {cfg.label}
                    </button>
                  ))}
                  <button
                    data-testid="bulk-ashby-btn"
                    onClick={bulkCreateAshby}
                    disabled={bulkAshbyLoading}
                    className="text-xs text-sky-600 hover:text-sky-800 font-body font-semibold flex items-center gap-1 border border-sky-200 rounded-full px-3 py-1 hover:bg-sky-50 transition-colors disabled:opacity-50"
                  >
                    {bulkAshbyLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />} Create in Ashby
                  </button>
                  <button
                    data-testid="bulk-hqi-btn"
                    onClick={bulkUploadHqi}
                    disabled={bulkHqiLoading}
                    className="text-xs text-emerald-600 hover:text-emerald-800 font-body font-semibold flex items-center gap-1 border border-emerald-200 rounded-full px-3 py-1 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                  >
                    {bulkHqiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Upload to High Quality Role
                  </button>
                  <button
                    data-testid="bulk-draft-emails-btn"
                    onClick={bulkDraftEmails}
                    disabled={bulkEmailLoading}
                    className="text-xs text-violet-600 hover:text-violet-800 font-body font-semibold flex items-center gap-1 border border-violet-200 rounded-full px-3 py-1 hover:bg-violet-50 transition-colors disabled:opacity-50"
                  >
                    {bulkEmailLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Draft Emails
                  </button>
                  <button
                    data-testid="bulk-delete-btn"
                    onClick={bulkDelete}
                    className="text-xs text-red-500 hover:text-red-700 font-body font-semibold flex items-center gap-1 border border-red-200 rounded-full px-3 py-1 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                  <button
                    data-testid="bulk-clear-btn"
                    onClick={() => { setCheckedIds(new Set()); setSelectAllMatching(false); }}
                    className="ml-auto text-xs text-slate-500 hover:text-slate-700 font-body underline"
                  >
                    Clear selection
                  </button>
                  </div>
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        data-testid="select-all-checkbox"
                        checked={applications.length > 0 && checkedIds.size === applications.length}
                        onChange={toggleAll}
                        className="w-4 h-4 rounded border-slate-300 text-ez-blue focus:ring-ez-blue cursor-pointer"
                      />
                    </TableHead>
                    <TableHead className="font-semibold text-ez-teal font-heading">Name</TableHead>
                    <TableHead className="font-semibold text-ez-teal font-heading">Email</TableHead>
                    <TableHead className="font-semibold text-ez-teal font-heading text-center">
                      <button
                        data-testid="sort-by-score"
                        onClick={() => toggleSort("score")}
                        className="inline-flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer"
                      >
                        Score
                        {sortCol === "score" && sortDir === "desc" ? <ArrowDown className="w-3.5 h-3.5" /> : sortCol === "score" && sortDir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold text-ez-teal font-heading">Status</TableHead>
                    <TableHead className="font-semibold text-ez-teal font-heading">
                      <button
                        data-testid="sort-by-date"
                        onClick={() => toggleSort("date")}
                        className="inline-flex items-center gap-1 hover:text-blue-600 transition-colors cursor-pointer"
                      >
                        Date
                        {sortCol === "date" && sortDir === "desc" ? <ArrowDown className="w-3.5 h-3.5" /> : sortCol === "date" && sortDir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold text-ez-teal font-heading text-center">Ashby Link</TableHead>
                    <TableHead className="font-semibold text-ez-teal font-heading text-center">LinkedIn</TableHead>
                    <TableHead className="font-semibold text-ez-teal font-heading text-center">Started Working</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map(app => (
                    <TableRow key={app.id} className={`hover:bg-slate-50/50 ${checkedIds.has(app.id) ? "bg-blue-50/40" : ""}`} data-testid={`app-row-${app.id}`}>
                      <TableCell>
                        <input
                          type="checkbox"
                          data-testid={`checkbox-${app.id}`}
                          checked={checkedIds.has(app.id)}
                          onChange={() => toggleCheck(app.id)}
                          className="w-4 h-4 rounded border-slate-300 text-ez-blue focus:ring-ez-blue cursor-pointer"
                        />
                      </TableCell>
                      <TableCell className="font-medium text-ez-teal font-heading">
                        <button
                          data-testid={`name-btn-${app.id}`}
                          onClick={() => openApp(app)}
                          className="hover:underline text-left cursor-pointer"
                        >
                          {app.full_name}
                        </button>
                        {app.job_type === "sales" && (
                          <span className="ml-1.5 inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700 align-middle" data-testid={`type-badge-${app.id}`}>SALES</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600 font-body text-sm">{app.email}</TableCell>
                      <TableCell className="text-center">
                        <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full border text-sm font-bold font-heading ${getScoreColor(app.score_total || 0)}`} data-testid={`score-${app.id}`}>
                          {Math.round(app.score_total || 0)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_CONFIG[app.status]?.color} border rounded-full px-3 py-0.5 text-xs font-medium`} data-testid={`status-badge-${app.id}`}>
                          {STATUS_CONFIG[app.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm font-body">{formatDate(app.submitted_at)}</TableCell>
                      <TableCell className="text-center">
                        {app.ashby_candidate_id && (
                          <a
                            href={`https://app.ashbyhq.com/home/upcoming/right-side/candidates/${app.ashby_candidate_id}/applications/${app.ashby_application_id || ''}/feed`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View in Ashby"
                            data-testid={`ashby-icon-${app.id}`}
                            className="inline-flex text-sky-500 hover:text-sky-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {app.linkedin_url && (
                          <a
                            href={app.linkedin_url.startsWith("http") ? app.linkedin_url : `https://${app.linkedin_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="LinkedIn Profile"
                            data-testid={`linkedin-icon-${app.id}`}
                            className="inline-flex text-blue-500 hover:text-blue-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </TableCell>
                      <TableCell className="text-center text-slate-600 text-sm font-body">
                        {app.earliest_work_year || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </div>
        </>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => closeApp()}>
        <DialogContent className="max-w-2xl rounded-2xl p-0 overflow-hidden" data-testid="application-detail-dialog">
          {selected && (
            <>
              <div className="bg-ez-surface px-6 py-5 border-b border-slate-100">
                <DialogHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <DialogTitle className="text-xl font-bold text-ez-teal font-heading" data-testid="detail-name">
                        {selected.full_name}
                      </DialogTitle>
                      <DialogDescription className="text-slate-500 font-body">
                        Applied {formatDate(selected.submitted_at)} · {selected.email} · {selected.phone}
                      </DialogDescription>
                    </div>
                    <div className="flex flex-col items-center ml-4" data-testid="detail-score-ring">
                      <svg width="56" height="56" viewBox="0 0 56 56">
                        <circle cx="28" cy="28" r="24" fill="none" stroke="#e2e8f0" strokeWidth="5" />
                        <circle
                          cx="28" cy="28" r="24" fill="none"
                          stroke={getScoreRingColor(selected.score_total || 0)}
                          strokeWidth="5" strokeLinecap="round"
                          strokeDasharray={`${((selected.score_total || 0) / 85) * 150.8} 150.8`}
                          transform="rotate(-90 28 28)"
                        />
                        <text x="28" y="32" textAnchor="middle" className="font-heading font-bold" fontSize="16" fill={getScoreRingColor(selected.score_total || 0)}>
                          {Math.round(selected.score_total || 0)}
                        </text>
                      </svg>
                      <span className="text-[10px] text-slate-400 font-body mt-0.5">Score</span>
                    </div>
                  </div>
                </DialogHeader>
              </div>
              <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
                {/* Score Breakdown */}
                {selected.score_breakdown && Object.keys(selected.score_breakdown).length > 0 && (
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-testid="score-breakdown">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 font-body flex items-center gap-1.5">
                      <Gauge className="w-3.5 h-3.5" /> Score Breakdown
                    </p>

                    {/* Prerequisites warning */}
                    {(!selected.job_type || selected.job_type === "guide") && selected.score_breakdown.prereqs_met === 0 && (
                      <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs text-red-700 font-body font-semibold">
                          Prerequisites not met — score capped at 10
                        </p>
                        <p className="text-xs text-red-500 font-body mt-0.5">
                          {!selected.can_work_25_hours && "Cannot work 25+ hrs/week. "}
                          {!selected.okay_with_1099 && "Not okay with 1099."}
                        </p>
                      </div>
                    )}

                    {selected.job_type === "sales" && selected.score_breakdown.disqualified && (
                      <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-xs text-red-700 font-body font-semibold">
                          Disqualified — score set to 0
                        </p>
                        <p className="text-xs text-red-500 font-body mt-0.5">
                          {(selected.score_breakdown.dq_reasons || []).map(r => ({
                            cannot_work_full_time: "Cannot work full-time",
                            no_outbound_experience: "No outbound experience",
                            low_call_volume: "Low call volume",
                            no_remote_experience: "No remote experience",
                            no_references: "No references",
                          }[r] || r)).join(". ")}.
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      {(selected.job_type === "sales" ? [
                        { key: "resume_llm", label: "Resume", max: 90, ai: true },
                        { key: "why_seniors", label: "Why Seniors", max: 10, ai: true },
                      ] : [
                        { key: "gpa", label: "GPA", max: 20 },
                        { key: "test_score", label: "Test Score", max: 20 },
                        { key: "college", label: "College", max: 20, ai: true },
                        { key: "resume_llm", label: "Resume", max: 20, ai: true },
                        { key: "short_answers", label: "Bonus", max: 5, ai: true },
                      ]).map(item => {
                        const val = selected.score_breakdown[item.key] || 0;
                        const pct = item.max > 0 ? (val / item.max) * 100 : 0;
                        const isZero = val === 0;
                        return (
                          <div key={item.key} className={`flex items-center gap-2 ${isZero ? "opacity-50" : ""}`}>
                            <span className="text-xs font-body w-32 text-slate-600 flex items-center gap-1">
                              {item.ai && <Sparkles className="w-3 h-3 text-violet-500" />}
                              {item.label}
                            </span>
                            <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-300"
                                style={{
                                  width: `${Math.min(pct, 100)}%`,
                                  backgroundColor: item.ai
                                    ? "#8b5cf6"
                                    : pct >= 75 ? "#10b981" : pct >= 40 ? "#d97706" : pct > 0 ? "#ef4444" : "#e2e8f0",
                                }}
                              />
                            </div>
                            <span className="text-xs font-heading font-semibold text-slate-700 w-12 text-right">
                              {isZero ? "—" : `${val}/${item.max}`}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* AI Reasoning */}
                    {(selected.resume_llm_reasoning || selected.text_ai_result || selected.college_rank_reason) && (
                      <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                        {selected.resume_llm_reasoning && (
                          <div>
                            <p className="text-xs font-semibold text-violet-600 font-body flex items-center gap-1 mb-1">
                              <Sparkles className="w-3 h-3" /> Resume AI ({selected.resume_llm_raw}/100)
                            </p>
                            <p className="text-xs text-slate-600 font-body leading-relaxed italic" data-testid="llm-reasoning">
                              "{selected.resume_llm_reasoning}"
                            </p>
                          </div>
                        )}
                        {selected.text_ai_result?.short_answers_reasoning && (
                          <div>
                            <p className="text-xs font-semibold text-violet-600 font-body flex items-center gap-1 mb-1">
                              <Sparkles className="w-3 h-3" /> Bonus AI ({selected.text_ai_result.short_answers_score}/100)
                            </p>
                            <p className="text-xs text-slate-600 font-body leading-relaxed italic" data-testid="short-answers-ai-reasoning">
                              "{selected.text_ai_result.short_answers_reasoning}"
                            </p>
                          </div>
                        )}
                        {(selected.college_rank_reason || selected.text_ai_result?.college_reasoning) && (
                          <div>
                            <p className="text-xs font-semibold text-violet-600 font-body flex items-center gap-1 mb-1">
                              <Sparkles className="w-3 h-3" /> College {selected.college_rank_reason ? "Rank" : "AI"} ({selected.score_breakdown?.college || 0}/20)
                            </p>
                            <p className="text-xs text-slate-600 font-body leading-relaxed italic" data-testid="college-ai-reasoning">
                              "{selected.college_rank_reason || selected.text_ai_result.college_reasoning}"
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Score with AI Button */}
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <Button
                        data-testid="score-resume-btn"
                        variant="outline"
                        size="sm"
                        onClick={() => scoreResume(selected.id)}
                        disabled={scoringId === selected.id}
                        className="rounded-lg border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                      >
                        {scoringId === selected.id ? (
                          <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Scoring...</>
                        ) : (selected.resume_llm_reasoning || selected.text_ai_result) ? (
                          <><Sparkles className="w-4 h-4 mr-1.5" /> Re-score with AI</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-1.5" /> Score with AI</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {selected.state_restricted && (
                  <p
                    className="text-xs text-amber-700 italic font-body"
                    data-testid="state-restricted-note"
                  >
                    Note: Candidate resides in an ineligible state{selected.state_restricted_state ? ` (${selected.state_restricted_state})` : ""}.
                  </p>
                )}

                {selected.hqi_ashby_application_id && (
                  <p
                    className="text-xs text-emerald-700 italic font-body"
                    data-testid="hqi-uploaded-note"
                  >
                    Uploaded to High Quality Inbound role{selected.hqi_created_at ? ` on ${new Date(selected.hqi_created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}` : ""}{selected.hqi_uploaded_by_admin ? " (manually pushed)" : ""}.
                  </p>
                )}

                {selected.ashby_skipped_existing && (
                  <p
                    className="text-xs text-slate-600 italic font-body"
                    data-testid="ashby-skipped-existing-note"
                  >
                    Ashby automation skipped &mdash; this candidate already had an Ashby application on another submission{selected.ashby_skipped_existing_candidate_id ? ` (candidate ${selected.ashby_skipped_existing_candidate_id.slice(0, 8)}…)` : ""}.
                  </p>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {(!selected.job_type || selected.job_type === "guide") ? (
                    <>
                      <DetailField label="Can work 25+ hrs/week" value={selected.can_work_25_hours ? "Yes" : "No"} />
                      <DetailField label="Okay with 1099" value={selected.okay_with_1099 ? "Yes" : "No"} />
                      <DetailField label="Loves talking to seniors" value={selected.loves_talking_to_seniors ? "Yes" : "No"} />
                      <DetailField label="College" value={selected.college_name || "Not provided"} />
                      <DetailField label="Undergrad GPA" value={
                        selected.undergrad_gpa
                          ? selected.gpa_scale
                            ? `${selected.undergrad_gpa} / ${selected.gpa_scale}`
                            : selected.undergrad_gpa
                          : "Not provided"
                      } />
                      <DetailField label="Test Score" value={selected.standardized_score || "Not provided"} />
                    </>
                  ) : (
                    <>
                      <DetailField label="Can work full-time (40 hrs)" value={selected.can_work_full_time ? "Yes" : "No"} />
                      <DetailField label="Outbound call experience" value={selected.outbound_call_experience ? "Yes" : "No"} />
                      <DetailField label="Calls per day" value={selected.calls_per_day || "Not provided"} />
                      <DetailField label="Worked remotely" value={selected.worked_remotely ? "Yes" : "No"} />
                      <DetailField label="Can work independently" value={selected.can_work_independently ? "Yes" : "No"} />
                      <DetailField label="Timezone / State" value={selected.timezone_or_state || "Not provided"} />
                    </>
                  )}
                  <DetailField label="Resume" value={selected.resume_filename || "Not uploaded"} isResume resumeId={selected.id} onDownload={downloadResume} />
                </div>

                {selected.job_type === "sales" && selected.references && selected.references.length > 0 && (
                  <div className="bg-ez-surface rounded-xl p-4 border border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 font-body">References</p>
                    <p className="text-slate-700 font-body text-sm">
                      {selected.references[0]?.willing === true ? "Willing to provide references" : selected.references[0]?.name ? `${selected.references[0].name}` : "Not provided"}
                    </p>
                  </div>
                )}

                {(!selected.job_type || selected.job_type === "guide") && selected.why_love_seniors && (
                  <div className="bg-ez-surface rounded-xl p-4 border border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 font-body">Why they love seniors</p>
                    <p className="text-slate-700 font-body leading-relaxed italic" data-testid="detail-why-seniors">
                      "{selected.why_love_seniors}"
                    </p>
                  </div>
                )}

                {selected.job_type === "sales" && selected.why_like_seniors && (
                  <div className="bg-ez-surface rounded-xl p-4 border border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 font-body">Why they like talking to seniors</p>
                    <p className="text-slate-700 font-body leading-relaxed italic" data-testid="detail-why-seniors">
                      "{selected.why_like_seniors}"
                    </p>
                  </div>
                )}

                {/* Personalized Email Draft */}
                {selected.personalized_email_draft && (
                  <div className="bg-violet-50/50 rounded-xl p-4 border border-violet-100" data-testid="email-draft-section">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-violet-600 uppercase tracking-wider font-body flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" /> Drafted Email
                      </p>
                      <button
                        data-testid="copy-email-btn"
                        onClick={() => copyEmailToClipboard(selected.personalized_email_draft)}
                        className="text-xs text-violet-500 hover:text-violet-700 font-body flex items-center gap-1 border border-violet-200 rounded-lg px-2 py-1 hover:bg-violet-100 transition-colors"
                      >
                        {emailCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {emailCopied ? "Copied" : "Copy"}
                      </button>
                    </div>
                    <pre className="text-sm text-slate-700 font-body leading-relaxed whitespace-pre-wrap" data-testid="email-draft-content">
                      {selected.personalized_email_draft}
                    </pre>
                  </div>
                )}

                {/* Ashby Action */}
                {!selected.ashby_candidate_id ? (
                  <div className="pt-3 border-t border-slate-100">
                    <Button
                      data-testid="create-ashby-btn"
                      variant="outline"
                      size="sm"
                      onClick={() => createInAshby(selected.id)}
                      disabled={ashbyLoadingId === selected.id}
                      className="rounded-lg border-sky-200 text-sky-700 hover:bg-sky-50 hover:text-sky-800"
                    >
                      {ashbyLoadingId === selected.id ? (
                        <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Creating...</>
                      ) : (
                        <><ExternalLink className="w-4 h-4 mr-1.5" /> Create in Ashby</>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="pt-3 border-t border-slate-100 flex items-center gap-3">
                    <p className="text-xs text-emerald-600 font-body flex items-center gap-1.5" data-testid="ashby-created-badge">
                      <CheckSquare className="w-3.5 h-3.5" /> Created in Ashby
                    </p>
                    {!selected.ashby_willo_moved && (
                      <Button
                        data-testid="move-to-willo-btn"
                        variant="outline"
                        size="sm"
                        onClick={() => moveToWillo(selected.id)}
                        disabled={willoLoadingId === selected.id}
                        className="rounded-lg border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                      >
                        {willoLoadingId === selected.id ? (
                          <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Moving...</>
                        ) : (
                          <><ArrowRightCircle className="w-4 h-4 mr-1.5" /> Move to Willo</>
                        )}
                      </Button>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
                  <p className="text-sm font-medium text-slate-600 font-body">Update Status:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                      <button
                        key={key}
                        data-testid={`set-status-${key}`}
                        onClick={() => updateStatus(selected.id, key)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-150 ${
                          selected.status === key
                            ? cfg.color + " ring-2 ring-offset-1 ring-slate-300"
                            : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        {cfg.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Delete - visually separated */}
                <div className="pt-4 mt-2 border-t border-red-100">
                  <button
                    data-testid="delete-application-btn"
                    onClick={() => deleteApplication(selected.id)}
                    className="text-xs text-red-400 hover:text-red-600 font-body flex items-center gap-1 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete this application
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Config Dialog */}
      <Dialog open={showEmailConfig} onOpenChange={setShowEmailConfig}>
        <DialogContent className="max-w-3xl rounded-2xl p-0 overflow-hidden" data-testid="email-config-dialog">
          <div className="bg-ez-surface px-6 py-5 border-b border-slate-100">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-ez-teal font-heading flex items-center gap-2">
                <Settings className="w-5 h-5" /> Email Drafting Config
              </DialogTitle>
              <DialogDescription className="text-slate-500 font-body text-sm">
                Edit the LLM system prompt and email template. Changes apply to all future drafts.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">
            {!emailConfig ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block font-body">
                    System Prompt (instructions to GPT-5.2)
                  </label>
                  <textarea
                    data-testid="email-system-prompt"
                    value={emailConfig.system_prompt}
                    onChange={(e) => setEmailConfig(prev => ({ ...prev, system_prompt: e.target.value }))}
                    rows={12}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm font-mono text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 resize-y"
                    placeholder="Enter the system prompt for the LLM..."
                  />
                  <p className="text-xs text-slate-400 font-body mt-1">
                    The LLM receives this as its system message. It controls tone, length, and style of the personalized intro.
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block font-body">
                    Email Template
                  </label>
                  <textarea
                    data-testid="email-template-input"
                    value={emailConfig.email_template}
                    onChange={(e) => setEmailConfig(prev => ({ ...prev, email_template: e.target.value }))}
                    rows={10}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm font-mono text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 resize-y"
                    placeholder="Enter the email template..."
                  />
                  <p className="text-xs text-slate-400 font-body mt-1">
                    Use <code className="bg-slate-100 px-1 rounded text-violet-600">{"{first_name}"}</code> and <code className="bg-slate-100 px-1 rounded text-violet-600">{"{personalized_intro}"}</code> as placeholders.
                  </p>
                </div>
              </>
            )}
          </div>
          <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-3">
            <Button
              data-testid="email-config-cancel"
              variant="outline"
              size="sm"
              onClick={() => setShowEmailConfig(false)}
              className="rounded-lg"
            >
              Cancel
            </Button>
            <Button
              data-testid="email-config-save"
              size="sm"
              onClick={saveEmailConfig}
              disabled={emailConfigSaving || !emailConfig}
              className="rounded-lg bg-violet-600 hover:bg-violet-700 text-white"
            >
              {emailConfigSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              Save Config
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ashbyBackfillResult} onOpenChange={(o) => { if (!o) { setAshbyBackfillResult(null); setBackfillCopied(false); } }}>
        <DialogContent className="max-w-3xl" data-testid="ashby-backfill-result-dialog">
          <DialogHeader>
            <DialogTitle>
              {ashbyBackfillResult?.dry_run ? "Ashby Backfill — Dry Run" : "Ashby Backfill — Results"}
            </DialogTitle>
            <DialogDescription>
              {ashbyBackfillResult?.dry_run ? (
                <>Scanned <strong>{ashbyBackfillResult?.scanned}</strong> · would place <strong>{ashbyBackfillResult?.would_place ?? 0}</strong>. Mixpanel events fired with <code>dry_run: true</code>. No Ashby writes, no emails sent.</>
              ) : (
                <>
                  Scanned <strong>{ashbyBackfillResult?.scanned}</strong> ·
                  placed <strong className="text-emerald-600">{ashbyBackfillResult?.placed}</strong> ·
                  skipped <strong className="text-slate-600">{ashbyBackfillResult?.skipped_existing}</strong>
                  {ashbyBackfillResult?.failed ? <> · failed <strong className="text-rose-600">{ashbyBackfillResult.failed}</strong></> : null}
                  {" · "}welcome emails sent <strong className="text-indigo-600">{ashbyBackfillResult?.welcome_sent ?? 0}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {ashbyBackfillResult?.details?.length ? (
            <>
              <div className="flex justify-end mb-2">
                <Button
                  data-testid="copy-backfill-results-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const rows = ashbyBackfillResult.details.map(r =>
                      [r.name || "", r.email || "", r.app_id, r.result, r.welcome_email || "", r.error || ""].join("\t")
                    );
                    const header = "Name\tEmail\tApp ID\tResult\tWelcome Email\tError";
                    navigator.clipboard.writeText([header, ...rows].join("\n"));
                    setBackfillCopied(true);
                    setTimeout(() => setBackfillCopied(false), 1500);
                  }}
                  className="rounded-lg"
                >
                  {backfillCopied ? (
                    <><Check className="w-4 h-4 mr-1.5 text-emerald-600" /> Copied</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-1.5" /> Copy as TSV</>
                  )}
                </Button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto border border-slate-200 rounded-lg">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-50">
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Result</TableHead>
                      <TableHead>Welcome Email</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ashbyBackfillResult.details.map((r) => (
                      <TableRow key={r.app_id} data-testid={`backfill-row-${r.app_id}`}>
                        <TableCell className="font-medium text-slate-800">{r.name || "—"}</TableCell>
                        <TableCell className="text-slate-600 font-mono text-xs">{r.email || "—"}</TableCell>
                        <TableCell>
                          {r.result === "placed" && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Placed</Badge>}
                          {r.result === "would_place" && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Would place</Badge>}
                          {r.result === "skipped_existing" && <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Already in Ashby</Badge>}
                          {r.result === "failed" && (
                            <div>
                              <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Failed</Badge>
                              {r.error && <p className="text-xs text-rose-500 mt-1 font-mono">{r.error}</p>}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {!r.welcome_email && <span className="text-xs text-slate-400">—</span>}
                          {r.welcome_email === "sent" && <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">Sent</Badge>}
                          {r.welcome_email === "would_send" && <Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50 border border-amber-200">Would send</Badge>}
                          {r.welcome_email === "already_sent" && <Badge className="bg-slate-100 text-slate-500 hover:bg-slate-100">Already sent</Badge>}
                          {r.welcome_email && !["sent", "would_send", "already_sent"].includes(r.welcome_email) && (
                            <span className="text-xs text-slate-500 font-mono">{r.welcome_email}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500 py-4">No orphan candidates found in the last 2 weeks.</p>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!indeedEmailBackfillResult} onOpenChange={(o) => { if (!o) setIndeedEmailBackfillResult(null); }}>
        <DialogContent className="max-w-lg" data-testid="indeed-email-backfill-result-dialog">
          <DialogHeader>
            <DialogTitle>
              {indeedEmailBackfillResult?.dry_run ? "Invite Email Backfill — Dry Run" : "Invite Email Backfill — Results"}
            </DialogTitle>
            <DialogDescription>
              {indeedEmailBackfillResult?.dry_run
                ? "Counts only — no emails were sent and no resumes were classified."
                : "Emails sent via Postmark. Un-screened guide resumes were classified first."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2" data-testid="indeed-email-backfill-result-grid">
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Candidates (last 24h)</div>
              <div className="text-2xl font-semibold text-slate-800" data-testid="backfill-stat-candidates">{indeedEmailBackfillResult?.candidates ?? 0}</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500">{indeedEmailBackfillResult?.dry_run ? "Would send" : "Sent"}</div>
              <div className="text-2xl font-semibold text-emerald-600" data-testid="backfill-stat-sent">{indeedEmailBackfillResult?.sent ?? 0}</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Skipped — already applied</div>
              <div className="text-2xl font-semibold text-slate-600" data-testid="backfill-stat-applied">{indeedEmailBackfillResult?.already_applied ?? 0}</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-500">Skipped — screened out</div>
              <div className="text-2xl font-semibold text-slate-600" data-testid="backfill-stat-screened">{indeedEmailBackfillResult?.failed_pre_screen ?? 0}</div>
            </div>
            {indeedEmailBackfillResult?.dry_run && (indeedEmailBackfillResult?.needs_pre_screen ?? 0) > 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 col-span-2">
                <div className="text-xs text-amber-700">Need pre-screen (run "real" to classify &amp; send)</div>
                <div className="text-2xl font-semibold text-amber-600" data-testid="backfill-stat-needs-prescreen">{indeedEmailBackfillResult?.needs_pre_screen}</div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sentInvitesData} onOpenChange={(o) => { if (!o) { setSentInvitesData(null); setSentInvitesCopied(false); } }}>
        <DialogContent className="max-w-4xl" data-testid="sent-invites-dialog">
          <DialogHeader>
            <DialogTitle>Sent Invite Emails — last {sentInvitesData?.window_hours ?? 48}h</DialogTitle>
            <DialogDescription>
              {sentInvitesData?.summary?.total ?? 0} record(s) touched ·
              {" "}sent <strong className="text-emerald-600">{sentInvitesData?.summary?.sent ?? 0}</strong> ·
              {" "}failed <strong className="text-rose-600">{sentInvitesData?.summary?.failed ?? 0}</strong> ·
              {" "}duplicate <strong className="text-slate-600">{sentInvitesData?.summary?.duplicate ?? 0}</strong>
              {(sentInvitesData?.summary?.pending ?? 0) > 0 ? <> · pending <strong className="text-amber-600">{sentInvitesData.summary.pending}</strong></> : null}
            </DialogDescription>
          </DialogHeader>

          {sentInvitesData?.rows?.length ? (
            <>
              <div className="flex justify-end mb-2">
                <Button
                  data-testid="copy-sent-invites-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const header = "Name\tEmail\tJob\tOutcome\tSent At\tMessage ID\tError";
                    const lines = sentInvitesData.rows.map((r) =>
                      [r.name || "", r.email || "", r.job_type || "", r.outcome, r.sent_at || "", r.message_id || "", r.error || ""].join("\t")
                    );
                    navigator.clipboard.writeText([header, ...lines].join("\n"));
                    setSentInvitesCopied(true);
                    setTimeout(() => setSentInvitesCopied(false), 1500);
                  }}
                  className="rounded-lg"
                >
                  {sentInvitesCopied ? (
                    <><Check className="w-4 h-4 mr-1.5 text-emerald-600" /> Copied</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-1.5" /> Copy as TSV</>
                  )}
                </Button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto border border-slate-200 rounded-lg">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-50">
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Outcome</TableHead>
                      <TableHead>Sent At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sentInvitesData.rows.map((r) => (
                      <TableRow key={r.record_id} data-testid={`sent-invite-row-${r.record_id}`}>
                        <TableCell className="font-medium text-slate-800">{r.name || "—"}</TableCell>
                        <TableCell className="text-slate-600 font-mono text-xs">{r.email || "—"}</TableCell>
                        <TableCell>
                          {r.outcome === "sent" && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Sent</Badge>}
                          {r.outcome === "duplicate" && <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Duplicate</Badge>}
                          {r.outcome === "failed" && (
                            <div>
                              <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Failed</Badge>
                              {r.error && <p className="text-xs text-rose-500 mt-1 font-mono">{r.error}</p>}
                            </div>
                          )}
                          {r.outcome === "pending" && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending</Badge>}
                        </TableCell>
                        <TableCell className="text-slate-600 text-xs">
                          {r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500 py-4">No invite emails have been sent in the last {sentInvitesData?.window_hours ?? 48}h.</p>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!purgeResumesResult} onOpenChange={(o) => { if (!o) { setPurgeResumesResult(null); setPurgeCopied(false); } }}>
        <DialogContent className="max-w-3xl" data-testid="purge-resumes-result-dialog">
          <DialogHeader>
            <DialogTitle>
              {purgeResumesResult?.dry_run ? "Purge Old Resumes — Dry Run" : "Purge Old Resumes — Results"}
            </DialogTitle>
            <DialogDescription>
              {purgeResumesResult?.dry_run ? (
                <>
                  Scanned <strong>{purgeResumesResult?.scanned}</strong> ·
                  would purge <strong>{purgeResumesResult?.would_purge ?? 0}</strong> ·
                  would free <strong className="text-rose-600">{((purgeResumesResult?.would_free_bytes ?? 0) / 1024 / 1024).toFixed(2)} MB</strong>.
                  {" "}No writes performed.
                </>
              ) : (
                <>
                  Scanned <strong>{purgeResumesResult?.scanned}</strong> ·
                  purged <strong className="text-rose-600">{purgeResumesResult?.purged}</strong> ·
                  freed <strong className="text-rose-600">~{((purgeResumesResult?.freed_bytes ?? 0) / 1024 / 1024).toFixed(2)} MB</strong>.
                  {" "}Mongo reclaims internal space first; Atlas disk metric updates after the next checkpoint.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {purgeResumesResult?.details?.length ? (
            <>
              <div className="flex justify-end mb-2">
                <Button
                  data-testid="copy-purge-results-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const rows = purgeResumesResult.details.map(r =>
                      [r.name || "", r.email || "", r.app_id,
                       r.submitted_at || "", r.resume_filename || "",
                       (r.bytes / 1024).toFixed(1) + " KB"].join("\t")
                    );
                    const header = "Name\tEmail\tApp ID\tSubmitted\tFilename\tSize";
                    navigator.clipboard.writeText([header, ...rows].join("\n"));
                    setPurgeCopied(true);
                    setTimeout(() => setPurgeCopied(false), 1500);
                  }}
                  className="rounded-lg"
                >
                  {purgeCopied ? (
                    <><Check className="w-4 h-4 mr-1.5 text-emerald-600" /> Copied</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-1.5" /> Copy as TSV</>
                  )}
                </Button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto border border-slate-200 rounded-lg">
                <Table>
                  <TableHeader className="sticky top-0 bg-slate-50">
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="text-right">Size</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {purgeResumesResult.details.map((r) => (
                      <TableRow key={r.app_id} data-testid={`purge-row-${r.app_id}`}>
                        <TableCell className="font-medium text-slate-800">{r.name || "—"}</TableCell>
                        <TableCell className="text-slate-600 font-mono text-xs">{r.email || "—"}</TableCell>
                        <TableCell className="text-slate-500 text-xs">{r.submitted_at ? r.submitted_at.slice(0, 10) : "—"}</TableCell>
                        <TableCell className="text-right text-slate-600 font-mono text-xs">{(r.bytes / 1024).toFixed(1)} KB</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500 py-4">No old resumes to purge.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const _CREDENTIAL_LABELS = {
  bachelors_or_higher: "Bachelor's+",
  associates_or_technical_degree: "Associate's / Technical",
  college_coursework_no_degree: "College, no degree",
  vocational_certificate: "Vocational cert.",
  certification_or_licence: "Cert. / licence",
  high_school_only: "High school only",
  none_stated: "None stated",
  error: "Error",
};

const _CREDENTIAL_STYLES = {
  bachelors_or_higher: "bg-emerald-100 text-emerald-700 border-emerald-200",
  associates_or_technical_degree: "bg-emerald-100 text-emerald-700 border-emerald-200",
  college_coursework_no_degree: "bg-emerald-100 text-emerald-700 border-emerald-200",
  vocational_certificate: "bg-emerald-100 text-emerald-700 border-emerald-200",
  certification_or_licence: "bg-emerald-100 text-emerald-700 border-emerald-200",
  high_school_only: "bg-amber-100 text-amber-700 border-amber-200",
  none_stated: "bg-amber-100 text-amber-700 border-amber-200",
  error: "bg-red-100 text-red-700 border-red-200",
};

function formatCredential(v) {
  return _CREDENTIAL_LABELS[v] || v;
}

function getCredentialStyle(v) {
  return _CREDENTIAL_STYLES[v] || "bg-slate-100 text-slate-600 border-slate-200";
}


function AIReasoningRow({ label, score, maxScore, reasoning, testid }) {
  if (!reasoning) return null;
  const header = maxScore != null
    ? `${label} (${score != null ? score : "?"}/${maxScore})`
    : score != null ? `${label} (${score})` : label;
  return (
    <div>
      <p className="text-xs font-semibold text-violet-600 font-body flex items-center gap-1 mb-1">
        <Sparkles className="w-3 h-3" /> {header}
      </p>
      <p
        className="text-xs text-slate-600 font-body leading-relaxed italic"
        data-testid={testid}
      >
        &ldquo;{reasoning}&rdquo;
      </p>
    </div>
  );
}


function PrescreensTable({ prescreens, loading, onRefresh }) {
  const [selected, setSelected] = useState(null);
  const openResume = (indeedRefId) => {
    const token = getToken();
    // Same admin-JWT + query-param contract as /applications/{id}/resume so
    // the download opens directly without an interstitial fetch.
    const url = `${API}/admin/indeed-prescreens/${indeedRefId}/resume?token=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  // Support the legacy raw-array shape (older tests) and the current
  // { prescreens, truncated, limit } envelope from list_indeed_prescreens.
  const rows = Array.isArray(prescreens)
    ? prescreens
    : (prescreens?.prescreens || []);
  const truncated = !Array.isArray(prescreens) && !!prescreens?.truncated;
  const limit = !Array.isArray(prescreens) ? (prescreens?.limit || 0) : 0;
  // Client-side stat: what % of pre-screened applicants did we actually SMS?
  // Denominator = every row in view; numerator = rows with sms_sent_at set.
  // Kept simple (no job-type slicing) since the table itself is unfiltered.
  const smsSentCount = rows.filter(r => !!r.sms_sent_at).length;
  const smsSentPct = rows.length > 0 ? Math.round((smsSentCount / rows.length) * 100) : 0;
  return (
    <div
      className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
      data-testid="prescreens-table-container"
    >
      <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-ez-teal font-heading">Pre-Screens</h2>
          <span className="text-xs text-slate-400 font-body" data-testid="prescreens-count">
            {rows.length}
          </span>
          {rows.length > 0 && (
            <Badge
              className="bg-ez-blue/10 text-ez-blue border-ez-blue/20"
              title={`${smsSentCount} of ${rows.length} pre-screened applicants received the invite SMS`}
              data-testid="prescreens-sms-rate"
            >
              SMS sent: {smsSentPct}% ({smsSentCount}/{rows.length})
            </Badge>
          )}
          {truncated && (
            <Badge
              className="bg-amber-100 text-amber-700 border-amber-200"
              title={`Showing the newest ${limit} pre-screens; older records are hidden. Pagination not implemented.`}
              data-testid="prescreens-truncated"
            >
              Truncated
            </Badge>
          )}
        </div>
        <Button
          data-testid="prescreens-refresh-btn"
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
          className="rounded-lg"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>
      {loading && prescreens === null ? (
        <div className="p-8 text-center text-sm text-slate-500 font-body" data-testid="prescreens-loading">
          Loading pre-screens...
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500 font-body" data-testid="prescreens-empty">
          No pre-screens yet.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Candidate</TableHead>
              <TableHead>Credential</TableHead>
              <TableHead>SMS</TableHead>
              <TableHead>Resume</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => {
              const pre = row.pre_screen_ai_result || {};
              const credential = pre.credential;
              const credReasoning = pre.credential_reasoning;
              const preError = pre.error;
              const skippedBySkipList = typeof row.sms_skipped_reason === "string"
                && row.sms_skipped_reason.startsWith("credential_");
              const smsSent = !!row.sms_sent_at;
              const smsError = row.sms_error;
              return (
                <TableRow
                  key={row.id}
                  data-testid={`prescreen-row-${row.id}`}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => setSelected(row)}
                >
                  <TableCell>
                    <div className="font-medium text-slate-800 font-body">
                      {row.parsed_applicant_name || "-"}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">
                      {row.parsed_applicant_email || ""}
                    </div>
                    <div className="text-xs text-slate-400 font-body mt-0.5">
                      {(row.job_type || "guide") === "sales" ? "Sales" : "Guide"}
                      {row.parsed_job_title ? ` - ${row.parsed_job_title}` : ""}
                    </div>
                  </TableCell>
                  <TableCell>
                    {credential ? (
                      <Badge
                        className={`${getCredentialStyle(credential)} ${credReasoning ? "cursor-help" : ""}`}
                        title={credential === "error" ? (preError || "classifier failed") : (credReasoning || "")}
                        data-testid={`prescreen-credential-${row.id}`}
                      >
                        {formatCredential(credential)}
                      </Badge>
                    ) : (
                      <span className="text-xs text-slate-400 font-body">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {smsSent ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid={`prescreen-sms-sent-${row.id}`}>
                        Sent
                      </Badge>
                    ) : skippedBySkipList ? (
                      <Badge
                        className="bg-amber-100 text-amber-700 border-amber-200"
                        title={row.sms_skipped_reason}
                        data-testid={`prescreen-sms-skipped-${row.id}`}
                      >
                        Not sent
                      </Badge>
                    ) : smsError ? (
                      <Badge
                        title={smsError}
                        className="bg-red-100 text-red-700 border-red-200 cursor-help"
                        data-testid={`prescreen-sms-error-${row.id}`}
                      >
                        Error
                      </Badge>
                    ) : (
                      <span className="text-xs text-slate-400 font-body">pending</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.resume_key ? (
                      <button
                        data-testid={`prescreen-resume-btn-${row.id}`}
                        onClick={(e) => { e.stopPropagation(); openResume(row.id); }}
                        className="text-sm text-ez-blue hover:underline inline-flex items-center gap-1 font-body"
                      >
                        <Download className="w-3.5 h-3.5" />
                        {row.resume_filename || "resume.pdf"}
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400 font-body">no resume</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <PrescreenDetailDialog
        row={selected}
        onClose={() => setSelected(null)}
        onOpenResume={openResume}
      />
    </div>
  );
}


function PrescreenDetailDialog({ row, onClose, onOpenResume }) {
  const open = !!row;
  const pre = row?.pre_screen_ai_result || {};
  const credential = pre.credential;
  const credReasoning = pre.credential_reasoning;
  const preError = pre.error;
  const jobType = row?.job_type || "guide";
  const jobLabel = jobType === "sales" ? "Sales" : "Guide";
  const smsSent = !!row?.sms_sent_at;
  const skippedBySkipList = typeof row?.sms_skipped_reason === "string"
    && row.sms_skipped_reason.startsWith("credential_");
  const smsError = row?.sms_error;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-2xl rounded-2xl p-0 overflow-hidden"
        data-testid="prescreen-detail-dialog"
      >
        {row && (
          <div className="p-6">
            <DialogHeader>
              <DialogTitle
                className="text-xl font-bold text-ez-teal font-heading"
                data-testid="prescreen-detail-name"
              >
                {row.parsed_applicant_name || "-"}
              </DialogTitle>
              <DialogDescription className="text-slate-500 font-body">
                {row.parsed_applicant_email || ""}
                <span className="ml-2 text-slate-400">
                  {jobLabel}{row.parsed_job_title ? ` - ${row.parsed_job_title}` : ""}
                </span>
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 bg-slate-50 rounded-xl p-4 border border-slate-100">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  {credential ? (
                    <Badge
                      className={getCredentialStyle(credential)}
                      data-testid="prescreen-detail-credential-badge"
                    >
                      {formatCredential(credential)}
                    </Badge>
                  ) : (
                    <span className="text-xs text-slate-400 font-body">no classification</span>
                  )}
                  {smsSent ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="prescreen-detail-sms-badge">
                      SMS sent
                    </Badge>
                  ) : skippedBySkipList ? (
                    <Badge
                      className="bg-amber-100 text-amber-700 border-amber-200"
                      title={row.sms_skipped_reason}
                      data-testid="prescreen-detail-sms-badge"
                    >
                      SMS not sent
                    </Badge>
                  ) : smsError ? (
                    <Badge
                      className="bg-red-100 text-red-700 border-red-200"
                      title={smsError}
                      data-testid="prescreen-detail-sms-badge"
                    >
                      SMS error
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600 border-slate-200" data-testid="prescreen-detail-sms-badge">
                      SMS pending
                    </Badge>
                  )}
                </div>
                {row.resume_key && (
                  <button
                    data-testid="prescreen-detail-resume-btn"
                    onClick={() => onOpenResume(row.id)}
                    className="text-sm text-ez-blue hover:underline inline-flex items-center gap-1 font-body"
                  >
                    <Download className="w-3.5 h-3.5" />
                    {row.resume_filename || "resume.pdf"}
                  </button>
                )}
              </div>

              {(credReasoning || preError) && (
                <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                  <AIReasoningRow
                    label="Education Credential"
                    score={credential ? formatCredential(credential) : null}
                    maxScore={null}
                    reasoning={credential === "error" ? (preError || credReasoning) : credReasoning}
                    testid="prescreen-credential-reasoning"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function DetailField({ label, value, isResume, resumeId, onDownload }) {
  return (
    <div className="bg-white rounded-xl p-3 border border-slate-100">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1 font-body">{label}</p>
      {isResume && value !== "Not uploaded" ? (
        <button
          data-testid="download-resume-btn"
          onClick={() => onDownload(resumeId, value)}
          className="text-sm text-ez-blue hover:underline flex items-center gap-1 font-body"
        >
          <Download className="w-3.5 h-3.5" /> {value}
        </button>
      ) : (
        <p className="text-sm font-medium text-ez-teal font-heading">{value}</p>
      )}
    </div>
  );
}

function DbDebugPanel() {
  const [open, setOpen] = useState(false);
  const [indexes, setIndexes] = useState(null);
  const [ops, setOps] = useState(null);
  const [busy, setBusy] = useState(null);

  const fetchAll = useCallback(() => {
    // Fire each request independently: every panel updates the moment its own
    // endpoint resolves, so a slow/failing one neither delays nor blanks the others.
    const load = (path, setter, label) =>
      api.get(`${API}${path}`, { headers: authHeaders() })
        .then(res => setter(res.data))
        .catch(e => reportError(e, label));
    load("/admin/db/indexes", setIndexes, "Failed to load DB indexes");
    load("/admin/db/current-op", setOps, "Failed to load DB operations");
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchAll();
    const t = setInterval(fetchAll, 5000);
    return () => clearInterval(t);
  }, [open, fetchAll]);

  const buildIndex = async (name) => {
    setBusy(`build:${name}`);
    try {
      await api.post(`${API}/admin/db/indexes/${name}/build`, {}, { headers: authHeaders() });
      toast.success(`Build started: ${name}`);
      fetchAll();
    } catch (e) {
      reportError(e, `Failed to start build for ${name}`);
    } finally {
      setBusy(null);
    }
  };

  const dropIndex = async (collection, name) => {
    if (!window.confirm(
      `Drop index "${name}" on ${collection}?\n\n` +
      `This is destructive — queries relying on this index will fall back ` +
      `to collection scans until rebuilt.`
    )) return;
    setBusy(`drop:${name}`);
    try {
      await api.delete(`${API}/admin/db/indexes/${collection}/${name}`, { headers: authHeaders() });
      toast.success(`Dropped ${collection}.${name}`);
      fetchAll();
    } catch (e) {
      reportError(e, `Failed to drop ${collection}.${name}`);
    } finally {
      setBusy(null);
    }
  };


  const killOp = async (opid) => {
    if (!window.confirm(`Kill operation ${opid}?`)) return;
    setBusy(`kill:${opid}`);
    try {
      await api.post(`${API}/admin/db/kill-op/${opid}`, {}, { headers: authHeaders() });
      toast.success(`Killed op ${opid}`);
      fetchAll();
    } catch (e) {
      reportError(e, `Failed to kill op ${opid}`);
    } finally {
      setBusy(null);
    }
  };

  const compactCollection = async (name, freeBytes) => {
    const freeMb = (freeBytes || 0) / (1024 * 1024);
    if (!window.confirm(
      `Compact "${name}"?\n\n` +
      `Reclaimable free space: ${freeMb.toFixed(1)} MB.\n\n` +
      `This runs synchronously and may take up to a few minutes on large ` +
      `collections. On MongoDB 6+ it yields and does not block CRUD.`
    )) return;
    setBusy(`compact:${name}`);
    try {
      const res = await api.post(`${API}/admin/db/compact/${name}`, {}, { headers: authHeaders() });
      const freed = res.data?.bytesFreed ?? 0;
      if (freed > 0) {
        toast.success(`Compact reclaimed ${fmtBytes(freed)} on ${name}`);
      } else {
        // MongoDB 7 enforces freeSpaceTargetMB >= 20; below that compact is a
        // no-op even though it returns ok:1. Say so plainly.
        toast.info(
          `Compact ran on ${name} but reclaimed 0 bytes — MongoDB requires ` +
          `at least 20 MB of contiguous free space to reclaim any.`
        );
      }
      fetchAll();
    } catch (e) {
      reportError(e, `Compact on ${name} failed`);
    } finally {
      setBusy(null);
    }
  };

  const sat = ops?.saturation;
  const storage = ops?.storage;
  const collections = ops?.collections;
  const builds = indexes?.in_progress_builds || [];
  const buildByIndex = new Map();
  builds.forEach(b => (b.index_names || []).forEach(n => buildByIndex.set(n, b)));

  const fmtBytes = (n) => {
    if (n == null) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
    return `${(n / 1024 ** 3).toFixed(2)} GB`;
  };

  return (
    <>
      <Button
        data-testid="db-debug-btn"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="rounded-lg text-slate-600 border-slate-200 hover:bg-slate-50"
      >
        <Database className="w-4 h-4 mr-1.5" /> DB Debug
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="db-debug-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" /> Database Diagnostics
            </DialogTitle>
            <DialogDescription>
              Index management and cluster saturation. Auto-refreshes every 5s while open.
            </DialogDescription>
          </DialogHeader>

          {sat && (sat.read_tickets?.totalTickets != null ? (
            <div className="border border-slate-200 rounded-lg p-3 text-xs font-mono space-y-1">
              <div className="font-semibold text-slate-700 font-body">Cluster saturation</div>
              <div>read tickets:  out={sat.read_tickets?.out ?? "?"}  available={sat.read_tickets?.available ?? "?"}  total={sat.read_tickets?.totalTickets ?? "?"}</div>
              <div>write tickets: out={sat.write_tickets?.out ?? "?"}  available={sat.write_tickets?.available ?? "?"}  total={sat.write_tickets?.totalTickets ?? "?"}</div>
              <div>connections:   current={sat.connections?.current ?? "?"}  available={sat.connections?.available ?? "?"}</div>
              {sat.wt_cache && (
                <div>WT cache:      used={sat.wt_cache.used_pct ?? "?"}%  dirty={sat.wt_cache.dirty_pct ?? "?"}%  ({fmtBytes(sat.wt_cache.used_bytes)} / {fmtBytes(sat.wt_cache.max_bytes)})</div>
              )}
              <div className="text-slate-500">Tickets saturate when out → total. WT dirty &gt;20% triggers forced eviction (slow reads).</div>
            </div>
          ) : (
            <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs space-y-1">
              <div className="font-semibold text-amber-800 font-body">Cluster saturation unavailable</div>
              <div className="text-amber-700 font-body">
                The MongoDB user this app connects as lacks the <code className="font-mono bg-amber-100 px-1 rounded">clusterMonitor</code> role,
                so <code className="font-mono bg-amber-100 px-1 rounded">serverStatus</code> is denied. Storage, collection, and index
                diagnostics below still work — they only need normal read access.
              </div>
              <div className="text-amber-700 font-body">
                To enable: in Atlas → Database Access → edit this app's database user → add role
                <code className="font-mono bg-amber-100 px-1 rounded mx-1">clusterMonitor</code> on database <code className="font-mono bg-amber-100 px-1 rounded">admin</code>.
              </div>
            </div>
          ))}

          {storage?.db && (
            <div className="mt-4 border border-slate-200 rounded-lg p-3 text-xs font-mono space-y-1">
              <div className="font-semibold text-slate-700 font-body">Storage</div>
              {storage.db.fsUsedSize != null && storage.db.fsTotalSize ? (
                <div>
                  disk: {fmtBytes(storage.db.fsUsedSize)} / {fmtBytes(storage.db.fsTotalSize)}{" "}
                  <span className={storage.db.fsUsedSize / storage.db.fsTotalSize > 0.85 ? "text-red-600 font-semibold" : "text-slate-500"}>
                    ({Math.round(100 * storage.db.fsUsedSize / storage.db.fsTotalSize)}% used)
                  </span>
                </div>
              ) : (
                <div className="text-slate-500">disk: fsUsedSize/fsTotalSize unavailable (Atlas may restrict)</div>
              )}
              <div>data: {fmtBytes(storage.db.dataSize)} · storage: {fmtBytes(storage.db.storageSize)} · indexes: {fmtBytes(storage.db.indexSize)}</div>
              <div>collections: {storage.db.collections} · objects: {storage.db.objects?.toLocaleString()}</div>
            </div>
          )}

          {collections?.length > 0 && (
            <div className="mt-4">
              <div className="font-semibold text-slate-700 mb-2 font-body">Collections</div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs text-right">Docs</TableHead>
                    <TableHead className="text-xs text-right">Storage</TableHead>
                    <TableHead className="text-xs text-right">Free (compactable)</TableHead>
                    <TableHead className="text-xs text-right">Indexes</TableHead>
                    <TableHead className="text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {collections.map(c => {
                    const free = c.freeStorageSize || 0;
                    // MongoDB 7 enforces freeSpaceTargetMB >= 20 on compact.
                    // Anything below that reclaims nothing, so disable the
                    // button and tell the operator why in the tooltip.
                    const MB = 1024 * 1024;
                    const canCompact = free >= 20 * MB;
                    const busyThis = busy === `compact:${c.name}`;
                    const tooltip = free === 0
                      ? "Nothing to reclaim"
                      : canCompact
                        ? `Reclaim up to ${fmtBytes(free)} of free space`
                        : `MongoDB requires ≥20 MB of contiguous free space to compact — this collection has ${fmtBytes(free)}`;
                    return (
                      <TableRow key={c.name}>
                        <TableCell className="font-mono text-xs">{c.name}</TableCell>
                        <TableCell className="text-right text-xs">{c.count?.toLocaleString() ?? "?"}</TableCell>
                        <TableCell className="text-right text-xs">{fmtBytes(c.storageSize)}</TableCell>
                        <TableCell className={`text-right text-xs ${free > 10 * MB ? "text-amber-700 font-semibold" : ""}`}>
                          {fmtBytes(c.freeStorageSize)}
                        </TableCell>
                        <TableCell className="text-right text-xs">{fmtBytes(c.totalIndexSize)} ({c.nindexes})</TableCell>
                        <TableCell className="text-right">
                          <Button
                            data-testid={`compact-${c.name}-btn`}
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={!canCompact || busy !== null}
                            onClick={() => compactCollection(c.name, free)}
                            title={tooltip}
                          >
                            {busyThis ? "Running…" : "Compact"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <div className="text-xs text-slate-500 mt-1">
                "Free" is space inside collection files freed by deletes but not returned to the OS — a compact would reclaim it.
              </div>
            </div>
          )}

          <div className="mt-4">
            <div className="font-semibold text-slate-700 mb-1 font-body">Indexes</div>
            <div className="text-xs text-slate-500 mb-2">
              {indexes ? `${indexes.indexes.filter(i => !i.exists).length} missing of ${indexes.indexes.length}` : "loading…"}
              {builds.length ? ` · ${builds.length} building` : ""}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Name</TableHead>
                  <TableHead className="text-xs">Collection</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Usage</TableHead>
                  <TableHead className="text-xs text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {indexes?.indexes?.map(ix => {
                  const build = buildByIndex.get(ix.name);
                  const pct = build?.progress_total ? Math.round(100 * (build.progress_done || 0) / build.progress_total) : null;
                  const status = build
                    ? <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                        Building{pct != null ? ` ${pct}%` : ""}
                      </Badge>
                    : ix.exists
                      ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Built</Badge>
                      : <Badge className="bg-red-100 text-red-700 border-red-200">Missing</Badge>;
                  return (
                    <TableRow key={`${ix.collection}.${ix.name}`}>
                      <TableCell className="font-mono text-xs">{ix.name}</TableCell>
                      <TableCell className="font-mono text-xs">{ix.collection}</TableCell>
                      <TableCell className="text-xs">{status}</TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {ix.usage?.ops != null ? `${ix.usage.ops.toLocaleString()} ops` : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {!ix.exists && !build && (
                          <Button size="sm" onClick={() => buildIndex(ix.name)} disabled={!!busy}>
                            {busy === `build:${ix.name}` ? <Loader2 className="w-3 h-3 animate-spin" /> : "Build"}
                          </Button>
                        )}
                        {ix.exists && !build && ix.name !== "_id_" && (
                          <Button
                            data-testid={`drop-index-${ix.name}-btn`}
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => dropIndex(ix.collection, ix.name)}
                            disabled={!!busy}
                          >
                            {busy === `drop:${ix.name}` ? <Loader2 className="w-3 h-3 animate-spin" /> : "Drop"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {indexes?.orphan_indexes?.length > 0 && (
            <div className="mt-4">
              <div className="font-semibold text-amber-700 mb-2 font-body">
                Orphan indexes ({indexes.orphan_indexes.length}) — in DB but not in spec
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Collection</TableHead>
                    <TableHead className="text-xs">Usage</TableHead>
                    <TableHead className="text-xs text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {indexes.orphan_indexes.map(o => (
                    <TableRow key={`${o.collection}.${o.name}`}>
                      <TableCell className="font-mono text-xs">{o.name}</TableCell>
                      <TableCell className="font-mono text-xs">{o.collection}</TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {o.usage?.ops != null ? `${o.usage.ops.toLocaleString()} ops` : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {o.name !== "_id_" && (
                          <Button
                            data-testid={`drop-orphan-index-${o.name}-btn`}
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => dropIndex(o.collection, o.name)}
                            disabled={!!busy}
                          >
                            {busy === `drop:${o.name}` ? <Loader2 className="w-3 h-3 animate-spin" /> : "Drop"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="mt-4">
            <div className="font-semibold text-slate-700 mb-2 font-body">
              Active operations ({ops?.operations?.length ?? 0})
            </div>
            {sat && sat.read_tickets?.totalTickets == null && (
              <div className="text-xs text-slate-500 mb-2 font-body">
                Without <code className="font-mono">clusterMonitor</code>, this list shows only operations
                belonging to this app's database connection (typically the index builds you triggered from this
                panel). Other users' operations and internal Atlas ops are hidden.
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">opid</TableHead>
                  <TableHead className="text-xs">op</TableHead>
                  <TableHead className="text-xs">ns</TableHead>
                  <TableHead className="text-xs">running</TableHead>
                  <TableHead className="text-xs">command</TableHead>
                  <TableHead className="text-xs text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ops?.operations?.map(op => (
                  <TableRow key={op.opid}>
                    <TableCell className="font-mono text-xs">{op.opid}</TableCell>
                    <TableCell className="text-xs">{op.op}</TableCell>
                    <TableCell className="font-mono text-xs">{op.ns}</TableCell>
                    <TableCell className="text-xs">{op.running_secs}s{op.waitingForLock ? " 🔒" : ""}</TableCell>
                    <TableCell className="font-mono text-xs truncate max-w-md">{op.command_summary}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => killOp(op.opid)} disabled={!!busy}>
                        Kill
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
